/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Stack,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Box,
  MenuItem,
  Typography,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { Iconify } from 'src/components';
import { APPLE, appleInputSx } from './appleDesignTokens';

const selectSx = {
  minWidth: 130,
  '& .MuiOutlinedInput-root': {
    borderRadius: APPLE.inputRadius,
    bgcolor: APPLE.grayBg,
    fontSize: '0.8rem',
    fontFamily: APPLE.font,
    fontWeight: 500,
    transition: 'all 0.2s ease',
    '& fieldset': { borderColor: 'transparent' },
    '&:hover fieldset': { borderColor: APPLE.grayBorder },
    '&.Mui-focused fieldset': { borderColor: APPLE.accent, borderWidth: '2px' },
    '&.Mui-focused': { bgcolor: '#fff' },
  },
  '& .MuiSelect-select': { py: 0.875, pl: 1.5 },
};

const TemplateFilterToolbar = ({
  search,
  onSearchChange,
  filters,
  multiFilters = [],
  selectedCount = 0,
  bulkActions = [],
  extraActions = null,
}) => (
  <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}>
    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
      <TextField
        size="small"
        placeholder="Search templates..."
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        sx={{ minWidth: 200, flex: 1, fontFamily: APPLE.font, ...appleInputSx }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Iconify icon="eva:search-fill" sx={{ color: APPLE.grayText, width: 20, height: 20 }} />
            </InputAdornment>
          ),
        }}
      />
      {filters.map((f) => (
        <TextField
          key={f.label}
          select
          size="small"
          value={f.value}
          onChange={(e) => f.onChange(e.target.value)}
          sx={selectSx}
          SelectProps={{
            displayEmpty: true,
            renderValue: (val) => (
              <Typography component="span" sx={{ fontSize: '0.8rem', fontFamily: APPLE.font, fontWeight: 500, color: val ? 'text.primary' : APPLE.grayText }}>
                {val || f.label}
              </Typography>
            ),
            MenuProps: { PaperProps: { sx: { maxHeight: 300, borderRadius: '12px', mt: 0.5 } } },
          }}
        >
          <MenuItem value="">
            <em>All {f.label}</em>
          </MenuItem>
          {f.options.map((opt) => {
            // Accept either a plain string (legacy usage) or a { value, label }
            // object. Object form is needed whenever display labels can collide
            // across distinct filter values (e.g. two practices with the same name).
            const val = typeof opt === 'object' && opt !== null ? opt.value : opt;
            const label = typeof opt === 'object' && opt !== null ? opt.label : opt;
            return <MenuItem key={String(val)} value={val}>{label}</MenuItem>;
          })}
        </TextField>
      ))}
      {/* Multi-select filters — each renders as a MUI multi-select Select with
          checkboxes, matching the same outlined variant as the single-selects.
          Accepts: { label, value (array), onChange (fn taking array), options: [{value, label}] } */}
      {multiFilters && multiFilters.map((f) => {
        const hasValue = Array.isArray(f.value) && f.value.length > 0;
        return (
          <TextField
            key={f.label}
            select
            size="small"
            value={f.value || []}
            sx={{ ...selectSx, minWidth: hasValue ? 160 : 130 }}
            SelectProps={{
              multiple: true,
              displayEmpty: true,
              renderValue: (selected) => {
                if (!selected || selected.length === 0) {
                  return (
                    <Typography component="span" sx={{ fontSize: '0.8rem', fontFamily: APPLE.font, fontWeight: 500, color: APPLE.grayText }}>
                      {f.label}
                    </Typography>
                  );
                }
                const count = selected.length;
                return (
                  <Typography component="span" sx={{ fontSize: '0.8rem', fontFamily: APPLE.font, fontWeight: 600, color: 'text.primary' }}>
                    {f.label} ({count})
                  </Typography>
                );
              },
              MenuProps: { PaperProps: { sx: { maxHeight: 340, borderRadius: '12px', mt: 0.5 } } },
              onChange: (e) => {
                const val = e.target.value;
                f.onChange(typeof val === 'string' ? val.split(',') : val);
              },
            }}
          >
            {f.options.map((opt) => {
              const optVal = typeof opt === 'object' ? opt.value : opt;
              const optLabel = typeof opt === 'object' ? opt.label : opt;
              const checked = Array.isArray(f.value) && f.value.includes(optVal);
              return (
                <MenuItem key={String(optVal)} value={optVal} sx={{ py: 0.5 }}>
                  <Checkbox size="small" checked={checked} sx={{ p: 0.25, mr: 0.5, color: '#C4CDD5', '&.Mui-checked': { color: '#00AB55' } }} />
                  <ListItemText primary={optLabel} primaryTypographyProps={{ variant: 'body2' }} />
                </MenuItem>
              );
            })}
            {/* Show a clear-all option when values are selected */}
            {hasValue && [
              <MenuItem key="__divider__" disabled sx={{ borderTop: '1px solid rgba(145,158,171,0.12)', my: 0.5, py: 0 }} />,
              <MenuItem key="__clear__" onClick={() => f.onChange([])} sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                <Iconify icon="eva:close-fill" width={16} sx={{ mr: 0.5 }} />
                Clear {f.label}
              </MenuItem>,
            ]}
          </TextField>
        );
      })}
      <Box sx={{ flex: '0 0 auto' }}>
        {extraActions || null}
      </Box>
    </Stack>

    {selectedCount > 0 && bulkActions?.length > 0 && (
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{
        mt: 2,
        py: 1.25,
        px: 2,
        bgcolor: APPLE.accentLight,
        borderRadius: APPLE.cardRadius,
        border: `1px solid ${APPLE.accent}20`,
      }}>
        <Chip size="small" label={`${selectedCount} selected`} color="primary" sx={{ fontWeight: 600, fontFamily: APPLE.font }} />
        {bulkActions.map((action) => (
          <React.Fragment key={action.label}>
            {action.selectComponent || null}
            <Button
              size="small"
              variant="contained"
              onClick={action.onClick}
              disabled={action.disabled}
              startIcon={action.icon ? <Iconify icon={action.icon} width={16} /> : null}
              sx={{ borderRadius: APPLE.inputRadius, textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', fontFamily: APPLE.font }}
            >
              {action.loading ? 'Applying...' : action.label}
            </Button>
          </React.Fragment>
        ))}
      </Stack>
    )}
  </Box>
);

const filterOptionShape = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired,
  }),
]);

TemplateFilterToolbar.propTypes = {
  search: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  filters: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(filterOptionShape).isRequired,
  })).isRequired,
  // Multi-select filter configs — rendered as MUI multi-select Selects with checkboxes.
  // value is an array; onChange receives an array.
  multiFilters: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(filterOptionShape).isRequired,
  })),
  selectedCount: PropTypes.number,
  bulkActions: PropTypes.array,
  extraActions: PropTypes.node,
};


export default TemplateFilterToolbar;
